import { squashAtomCommandFailure } from "@afar-research/client-runtime/state/runtime";
import { scopedThreadKey } from "@afar-research/client-runtime/environment";
import { canRequestManualCompaction, isManualCompactionInFlight } from "@afar-research/afar";
import type { ScopedThreadRef } from "@afar-research/contracts";
import { useState } from "react";
import { providerConversationOperationSupported } from "../providerInstances";
import { useThreadProvider } from "./useThreadProvider";
import { threadEnvironment } from "../state/threads";
import { useAtomCommand } from "../state/use-atom-command";

/** Both entry points observe the same durable request, including after reconnect. */
export function useManualCompaction(ref: ScopedThreadRef | null) {
  const { thread, config, instanceId, driver } = useThreadProvider(ref);
  const compact = useAtomCommand(threadEnvironment.compact);
  const [requestingFor, setRequestingFor] = useState<string | null>(null);
  const key = ref ? scopedThreadKey(ref) : null;
  const supported =
    driver === "codex" &&
    instanceId !== undefined &&
    ref !== null &&
    providerConversationOperationSupported(config?.providers ?? [], instanceId, "manualCompaction");
  const inFlight =
    (requestingFor !== null && requestingFor === key) ||
    isManualCompactionInFlight(thread?.activities ?? []);
  const enabled =
    supported &&
    !inFlight &&
    thread !== null &&
    canRequestManualCompaction({
      sessionStatus: thread.session?.status,
      hasMessages: thread.messages.length > 0,
      latestTurnState: thread.latestTurn?.state,
      activities: thread.activities,
    });
  return {
    scopeKey: key,
    driver,
    disabledReason: enabled
      ? undefined
      : !supported
        ? "This Codex instance has not reported manual compaction support."
        : inFlight
          ? "Compaction is already in progress."
          : "Compaction needs a ready Codex session with an idle turn and Thread Queue.",
    supported,
    enabled,
    inFlight,
    request: async () => {
      if (!enabled || !ref || !thread) return;
      setRequestingFor(key);
      try {
        const result = await compact({
          environmentId: ref.environmentId,
          input: { threadId: ref.threadId, expectedRevision: thread.updatedAt },
        });
        if (result._tag === "Failure") throw squashAtomCommandFailure(result);
      } finally {
        setRequestingFor(null);
      }
    },
  };
}
